import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import "../css/Admin_css/admin-customize.css";
import COLORS from "../resources/colors/COLORS";
import {
  mdl,
  formatINR,
  formatDateDMY,
  numOrZero,
  PAYMENT_TYPE_OPTIONS,
  PAYROLL_PAYMENT_MODE_OPTIONS,
  PaymentSummaryMetrics,
  formatPaymentModeLabel,
  getPayrollModalStyles,
} from "./payrollMonthlyShared";

function emptyForm(defaultMode, balanceDue, netSalary, totalPaid) {
  return {
    payment_id: null,
    payment_type:
      totalPaid <= 0.009 && balanceDue >= netSalary - 0.009 ? "FINAL" : "PART",
    payment_mode: defaultMode,
    amount: balanceDue > 0.009 ? String(balanceDue) : "",
    payment_date: new Date().toISOString().slice(0, 10),
    transaction_ref: "",
    remarks: "",
  };
}

function normalizePaymentMode(mode, fallback) {
  const key = String(mode || "")
    .trim()
    .toUpperCase();
  const match = PAYROLL_PAYMENT_MODE_OPTIONS.find(
    (o) => o.value === key || o.label.toUpperCase() === key
  );
  return match ? match.value : fallback;
}

function paymentToForm(p, defaultMode) {
  const dateRaw = p?.payment_date;
  const dateStr =
    dateRaw && String(dateRaw).length >= 10
      ? String(dateRaw).slice(0, 10)
      : new Date().toISOString().slice(0, 10);
  return {
    payment_id: p?.id ?? null,
    payment_type: p?.payment_type || "PART",
    payment_mode: normalizePaymentMode(p?.payment_mode, defaultMode),
    amount:
      p?.amount != null && p?.amount !== ""
        ? String(numOrZero(p.amount))
        : "",
    payment_date: dateStr,
    transaction_ref: p?.transaction_ref || "",
    remarks: p?.remarks || "",
  };
}

const MdlPayRollUpdatePayment = ({
  show = false,
  actionClose,
  staffRecord = null,
  payments = [],
  summary = null,
  saving = false,
  saveError = "",
  onSave,
}) => {
  const [modalShow, setModalShow] = useState(show);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [localError, setLocalError] = useState("");

  const defaultMode = PAYROLL_PAYMENT_MODE_OPTIONS[0]?.value || "CASH";

  const netSalary = summary?.net_salary ?? numOrZero(staffRecord?.net_salary);
  const totalPaid = summary?.total_paid ?? numOrZero(staffRecord?.total_paid);
  const balanceDue = summary?.balance_due ?? numOrZero(staffRecord?.balance_due);

  const maxAmountForForm = useMemo(() => {
    if (!selectedPaymentId) return balanceDue;
    const row = payments.find((p) => String(p.id) === String(selectedPaymentId));
    const rowAmt = row ? numOrZero(row.amount) : 0;
    return balanceDue + rowAmt;
  }, [selectedPaymentId, payments, balanceDue]);

  const [form, setForm] = useState(() =>
    emptyForm(defaultMode, balanceDue, netSalary, totalPaid)
  );
  const wasOpenRef = useRef(false);

  useEffect(() => {
    setModalShow(show);
  }, [show]);

  useEffect(() => {
    if (!show) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    setSelectedPaymentId(null);
    setLocalError("");
    setForm(emptyForm(defaultMode, balanceDue, netSalary, totalPaid));
    // Intentionally run once when the modal opens (not when summary totals change during edit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show || selectedPaymentId != null) return;
    setForm(emptyForm(defaultMode, balanceDue, netSalary, totalPaid));
  }, [show, defaultMode, balanceDue, netSalary, totalPaid, selectedPaymentId]);

  useEffect(() => {
    if (!show || payments.length !== 1 || balanceDue > 0.009) return;
    if (selectedPaymentId != null) return;
    const p = payments[0];
    if (!p) return;
    setSelectedPaymentId(p.id);
    setForm(paymentToForm(p, defaultMode));
  }, [show, payments, balanceDue, selectedPaymentId, defaultMode]);

  const handleClose = () => {
    setModalShow(false);
    if (actionClose) actionClose();
  };

  const selectPayment = useCallback(
    (p) => {
      if (!p) {
        setSelectedPaymentId(null);
        setForm(emptyForm(defaultMode, balanceDue, netSalary, totalPaid));
        return;
      }
      setSelectedPaymentId(p.id);
      setForm(paymentToForm(p, defaultMode));
      setLocalError("");
    },
    [defaultMode, balanceDue, netSalary, totalPaid]
  );

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (saving || typeof onSave !== "function") return;
    if (payments.length > 0 && !form.payment_id && balanceDue <= 0.009) {
      setLocalError("Select a payment from the list above to edit it.");
      return;
    }
    const amount = numOrZero(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setLocalError("Enter a valid amount greater than zero.");
      return;
    }
    if (amount > maxAmountForForm + 0.009) {
      setLocalError(`Amount cannot exceed ${formatINR(maxAmountForForm)}.`);
      return;
    }
    setLocalError("");
    await onSave({
      payment_id: form.payment_id,
      payment_type: form.payment_type,
      payment_mode: form.payment_mode,
      amount,
      payment_date: form.payment_date,
      transaction_ref: form.transaction_ref.trim(),
      remarks: form.remarks.trim(),
    });
  };

  const staffLabel = staffRecord?.staff_name
    ? staffRecord.staff_name
    : staffRecord?.staff_id || "Staff";
  const staffMeta = [
    staffRecord?.staff_id,
    staffRecord?.salary_id,
    staffRecord?.year_month,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Modal
      show={modalShow}
      onHide={handleClose}
      animation={false}
      size="lg"
      aria-labelledby="payroll-update-payment-title"
      centered
      backdrop="static"
      backdropClassName="custom-backdrop"
      contentClassName="custom-modal-content payroll-mdl payroll-payment-mdl"
    >
      <style>{getPayrollModalStyles()}</style>
      <div
        style={{
          background: mdl.pageBg,
          borderRadius: mdl.radiusXl,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        <Modal.Header
          closeButton
          className="border-0"
          style={{
            background: `linear-gradient(115deg, ${COLORS.primary_color_shade8 || "#0277BD"} 0%, ${COLORS.primary_color_shade6 || "#0288d1"} 55%, ${COLORS.primary_color_shade5 || "#29b6f6"} 100%)`,
            color: COLORS.white,
            padding: "20px 24px",
            flexShrink: 0,
          }}
        >
          <div>
            <Modal.Title
              id="payroll-update-payment-title"
              style={{ fontSize: 19, fontWeight: 750, margin: 0, letterSpacing: "-0.02em" }}
            >
              Update payment
            </Modal.Title>
            <div className="mt-1" style={{ fontSize: 15, fontWeight: 600, opacity: 0.95 }}>
              {staffLabel}
            </div>
            {staffMeta ? (
              <div className="small mt-1" style={{ opacity: 0.82 }}>
                {staffMeta}
              </div>
            ) : null}
          </div>
        </Modal.Header>

        <Modal.Body
          style={{
            padding: "20px 24px",
            overflowY: "auto",
            flex: "1 1 auto",
            fontFamily: mdl.font,
          }}
        >
          <PaymentSummaryMetrics
            netSalary={netSalary}
            totalPaid={totalPaid}
            balanceDue={balanceDue}
          />

          {(saveError || localError) && (
            <div
              className="mb-3 py-2 px-3 small fw-semibold"
              style={{
                color: "#b91c1c",
                background: "rgba(254, 226, 226, 0.9)",
                borderRadius: mdl.radiusMd,
                border: "1px solid rgba(248, 113, 113, 0.45)",
              }}
            >
              {saveError || localError}
            </div>
          )}

          {payments.length > 0 ? (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span
                  className="small fw-bold text-uppercase"
                  style={{ color: mdl.muted, letterSpacing: "0.05em" }}
                >
                  Payment history
                </span>
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  className="rounded-pill fw-semibold"
                  onClick={() => selectPayment(null)}
                  disabled={saving || balanceDue <= 0.009}
                >
                  + New payment
                </Button>
              </div>
              {payments.map((p) => {
                const active = String(p.id) === String(selectedPaymentId);
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`pp-history-card w-100 text-start${active ? " pp-history-card--active" : ""}`}
                    onClick={() => selectPayment(p)}
                    disabled={saving}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <div className="fw-bold" style={{ color: mdl.text }}>
                          {formatINR(p.amount)}
                        </div>
                        <div className="small" style={{ color: mdl.muted }}>
                          {formatDateDMY(p.payment_date)} · {p.payment_type}
                        </div>
                      </div>
                      <span className="payroll-pay-mode-badge">
                        {formatPaymentModeLabel(p.payment_mode)}
                      </span>
                    </div>
                    {p.transaction_ref ? (
                      <div className="small mt-1" style={{ color: mdl.muted }}>
                        Ref: {p.transaction_ref}
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="payroll-pay-empty mb-3">
              No payments yet. Use the form below to record the first payment.
            </div>
          )}

          <div className="pp-form-panel">
            <p className="mb-3 fw-bold" style={{ color: mdl.text, fontSize: 14 }}>
              {form.payment_id ? "Edit payment" : "Record payment"}
            </p>

            <Form.Group className="mb-3">
              <Form.Label>Payment type</Form.Label>
              <div className="pp-segment" role="group" aria-label="Payment type">
                {PAYMENT_TYPE_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={`pp-segment-btn${form.payment_type === o.value ? " pp-segment-btn--active" : ""}`}
                    onClick={() => setForm((f) => ({ ...f, payment_type: o.value }))}
                    disabled={saving}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Payment mode</Form.Label>
              <div className="pp-mode-grid" role="group" aria-label="Payment mode">
                {PAYROLL_PAYMENT_MODE_OPTIONS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    className={`pp-mode-pill${form.payment_mode === m.value ? " pp-mode-pill--active" : ""}`}
                    onClick={() => setForm((f) => ({ ...f, payment_mode: m.value }))}
                    disabled={saving}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </Form.Group>

            <div className="row g-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={form.amount}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) {
                        setForm((f) => ({ ...f, amount: raw }));
                      }
                    }}
                    disabled={saving}
                    placeholder="0.00"
                  />
                  <Form.Text className="text-muted">
                    Max {formatINR(maxAmountForForm)}
                  </Form.Text>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Payment date</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.payment_date}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, payment_date: e.target.value }))
                    }
                    disabled={saving}
                  />
                </Form.Group>
              </div>
              <div className="col-12">
                <Form.Group>
                  <Form.Label>Transaction reference (optional)</Form.Label>
                  <Form.Control
                    value={form.transaction_ref}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, transaction_ref: e.target.value }))
                    }
                    disabled={saving}
                    placeholder="UPI ID, cheque no., NEFT ref…"
                  />
                </Form.Group>
              </div>
              <div className="col-12">
                <Form.Group className="mb-0">
                  <Form.Label>Remarks (optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.remarks}
                    onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
                    disabled={saving}
                  />
                </Form.Group>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer
          className="border-0 d-flex justify-content-between"
          style={{
            background: "#f8fafc",
            padding: "14px 24px",
            borderTop: `1px solid ${mdl.borderHairline}`,
          }}
        >
          <Button variant="light" className="fw-semibold" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            className="pp-footer-primary"
            disabled={saving}
            onClick={handleSubmit}
          >
            {saving ? "Saving…" : form.payment_id ? "Save changes" : "Record Payment"}
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default MdlPayRollUpdatePayment;
